import { Box2, Matrix3, Vector2 } from "three";
import ColorLib, { type ColorInput } from "tinycolor2";

import { isCanvasFontStyle } from "./custom_type_guards";
import { Vector2Like } from "type_aliases";

type ImageArgument = CanvasImageSource & { width: number, height: number };

export type CanvasFontStyle = {
  color: ColorLib.ColorInput,
  size: number,
  name: string
  weight: number | "normal" | "bold"
}

export default class CanvasHelper {
  constructor(canvas?: HTMLCanvasElement) {
    if (canvas) {
      this.canvas = canvas;
    } else {
      this.canvas = document.createElement("canvas");
    }
    this.context = this.canvas.getContext("2d");
    this.transform = new Matrix3().identity();
    this.homogeneousTransform = new Matrix3().identity();
    this.setTextStyle("#000", 10, "sans-serif");

    this.getActualCanvasSize();
  }

  /**
   * Sizes the canvas so that its internal pixel resolution = css resolution * devicePixelRatio (for e.g. retina screens)
   */
  private getActualCanvasSize() {
    const ratio = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    const styles = window.getComputedStyle(this.canvas);
    // These should be read only everywhere outside of this function
    // @ts-ignore
    this.width = rect?.width - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight) - parseFloat(styles.borderLeftWidth) - parseFloat(styles.borderRightWidth) || 1;
    // @ts-ignore
    this.height = rect?.height - parseFloat(styles.paddingTop) - parseFloat(styles.paddingBottom) - parseFloat(styles.borderTopWidth) - parseFloat(styles.borderBottomWidth) || 1;
    this.scale = ratio;
    this.resize(this.width, this.height);
    // This ensures that 1 pixel in the canvas = 1 css pixel
    this.transform.copy(new Matrix3().identity()).scale(ratio, ratio);
    this.updateContextTransform();
  };

  /**
   * Resizes the canvas to the provided width and height. Doesn't transform what the canvas is rendering.
   * Doesn't change the canvas css (so size on page may remain unchanged.)
   */
  resize(width: number, height: number) {
    this.canvas.width = width * this.scale;
    this.canvas.height = height * this.scale;
  }

  /**
   * Clears the canvas and draws the given image.
   *
   * Sizes the canvas to be the same size as the given image.
   */
  setCanvasImage(image: ImageArgument) {
    this.clear();
    this.canvas.width = image.width;
    this.canvas.height = image.height;
    this.drawImage(image, 0, 0);
  };

  /** Draws the given image full sized and at the given x and y coordinates. */
  drawImage(image: ImageArgument, x: number, y: number, opacity: number = 1) {
    this.context.save();
    this.context.globalAlpha = opacity;
    this.context.drawImage(image, x, y);
    this.context.restore();
  }

  /**
   * Draws an image at the provided coordinates.
   *
   * Note that if the imageBox has a different aspect ratio than the image, the image will be distorted.
   */
  drawImageInBox(image: ImageArgument, imageBox: Box2) {
    this.context.drawImage(image,
      0,
      0,
      image.width,
      image.height,
      imageBox.min.x,
      imageBox.min.y,
      imageBox.max.x - imageBox.min.x,
      imageBox.max.y - imageBox.min.y);
  };

  drawZoomedImageInBox(image: ImageArgument, imageBox: Box2, anchor: Vector2, zoom: number = 4) {
    const imageWidth = image.width;
    const imageHeight = image.height;
    const dWidth = imageBox.max.x - imageBox.min.x;
    const dHeight = imageBox.max.y - imageBox.min.y;
    const widthRatio = imageWidth / dWidth;
    const heightRatio = imageHeight / dHeight;
    const sWidth = (dWidth * widthRatio) / zoom;
    const sHeight = (dHeight * heightRatio) / zoom;
    const sx = Math.max((anchor.x * widthRatio) - (sWidth / 2), 0);
    const sy = Math.max((anchor.y * heightRatio) - (sHeight / 2), 0);

    this.context.drawImage(image,
      sx,
      sy,
      sWidth,
      sHeight,
      imageBox.min.x,
      imageBox.min.y,
      dWidth,
      dHeight);
  };

  /**
   *
   */
  drawImageInCroppedBox(image: ImageArgument, anchor: Vector2, imageBox: Box2) {
    //drawImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight)
    let width = imageBox.max.x - imageBox.min.x;
    let height = imageBox.max.y - imageBox.min.y;
    let sx = Math.max(anchor.x - Math.floor(width / 2), 0);
    let sy = Math.max(anchor.y - Math.floor(height / 2), 0);
    this.context.drawImage(
      image,
      sx,
      sy,
      width,
      height,
      imageBox.min.x,
      imageBox.min.y,
      width,
      height
    );
  }

  clear() {
    this.context.save();
    // reset the transform so that the rect we're clearing doesn't get transformed as well;
    this.setTransform(new Matrix3());
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.context.restore();
  }

  /**
   * Returns a data url for the current content of the canvas
   * @param type - defaults to "image/png"
   */
  getDataUrl(type?: string) {
    return this.canvas.toDataURL(type || "image/png");
  }

  /**
   * Calculate the lines of text given the words and the max allowed width of the text
   * @param text The text to line wrap (breaks on whitespace)
   * @param maxLineWidth The max allowed width of the text in canvas pixels. The canvas' current transform will
   *                     be applied to the text during this calculation.
   * @returns An array of the lines of text, such that each line is as long as possible without exceeding the max width.
   */
  wrapText(text: string, maxLineWidth: number): string[] {
    let wrappedLines = [];

    let currentLineWords = [];
    for (let word of text.split(" ")) {
      currentLineWords.push(word);
      let currentLineWidth = this.context.measureText(currentLineWords.join(" ")).width;
      if (currentLineWidth >= maxLineWidth) {
        let lineLeftovers = currentLineWords.pop();
        wrappedLines.push(currentLineWords.join(" "));
        currentLineWords = [lineLeftovers];
      }
    }

    // Add whatever is left in the last line to the result
    if (currentLineWords.length) {
      wrappedLines.push(currentLineWords.join(" "));
    }

    return wrappedLines;
  }

  setTextStyle(style: CanvasFontStyle)
  setTextStyle(color: ColorLib.ColorInput, size: number, font: string, weight?: number | "bold" | "normal")
  setTextStyle(color: ColorLib.ColorInput | CanvasFontStyle, size?: number, font?: string, weight?: number | "bold" | "normal") {
    if (isCanvasFontStyle(color)) {
      size = color.size;
      font = color.name;
      weight = color.weight;
      color = color.color;
    }
    if (color != null) {
      this.textColor = new ColorLib(color);
    }
    if (size != null) {
      this.textSize = size;
    }
    if (font != null) {
      this.textFont = font;
    }
    if (weight != null) {
      this.textWeight = weight || null;
    }
    if (size != null || font != null || weight != null) {
      this.context.font = this.buildFontString(this.textSize, this.textFont, this.textWeight);
    }
  }

  writeText(text: string, positionX: number, positionY: number, alignment: CanvasTextBaseline = "alphabetic", justification: CanvasTextAlign = "start", color?: ColorLib.ColorInput, size?: number, font?: string, weight?: number | "bold" | "normal", stroke?: ColorLib.ColorInput) {
    this.context.save();
    if (alignment != null) {
      this.context.textBaseline = alignment;
    }
    if (justification != null) {
      this.context.textAlign = justification;
    }
    this.drawText(text, positionX, positionY, color, size, font, weight, stroke);
    this.context.restore();
  }

  private drawText(text: string, x: number, y: number, color: ColorLib.ColorInput, size: number, font: string, weight: number | "bold" | "normal", stroke: ColorLib.ColorInput) {
    this.setTextStyle(color, size, font, weight);
    if (this.textColor != null) {
      this.context.fillStyle = this.textColor.toRgbString();
    } else {
      this.context.fillStyle = new ColorLib("#000").toRgbString();
    }
    this.context.fillText(text, x, y);
    if (stroke != null) {
      this.context.strokeStyle = new ColorLib(stroke).toRgbString();
      this.context.strokeText(text, x, y);
    }
  }

  private buildFontString(size: number, font: string, weight: number | "normal" | "bold") {
    let fontString = "";
    if (weight != null) {
      fontString = `${weight} `;
    } else if (this.textWeight != null) {
      fontString = `${this.textWeight} `;
    }
    if (size != null) {
      fontString += `${size}px `;
    } else {
      fontString += `${this.textSize}px `;
    }
    if (font != null) {
      fontString += font;
    } else {
      fontString += this.textFont;
    }

    return fontString;
  }

  /** Draws a circle with all internal transforms applied */
  drawCircle(x: number, y: number, radius: number, fill?: ColorLib.ColorInput, stroke?: ColorLib.ColorInput, lineWidth?: number, lineStyle?: "solid" | number[]) {
    this.context.save();
    const path = new Path2D();
    path.ellipse(x, y, radius, radius, 0, 0, Math.PI * 2);
    this.drawPath(path, fill, stroke, lineWidth, lineStyle);
    this.context.restore();
    return path;
  }

  /** Draws a circle with all transforms applied, but scaled to have the exact radius provided in css pixels */
  drawFixedCircle(x: number, y: number, radius: number, fill?: ColorLib.ColorInput, stroke?: ColorLib.ColorInput, lineWidth?: number, lineStyle?: "solid" | number[]) {
    this.context.save();
    const path = this.getFixedCircle(x, y, radius);
    this.drawPath(path, fill, stroke, lineWidth, lineStyle);
    this.context.restore();
    return path;
  }

  /** Returns the {@link Path2D} for a circle with all transforms applied, but scaled to have the exact radius provided in css pixels */
  getFixedCircle(x: number, y: number, radius: number) {
    const scale = this.getScale();
    const path = new Path2D();
    path.ellipse(x, y, radius / scale.x, radius / scale.y, 0, 0, Math.PI * 2);
    return path;
  }

  drawRect(x: number, y: number, width: number, height: number, radius?: number, fill?: ColorLib.ColorInput, stroke?: ColorLib.ColorInput, lineWidth?: number, lineStyle?: "solid" | number[]) {
    this.context.save();
    const path = this.getRect(x, y, width, height, radius);
    this.drawPath(path, fill, stroke, lineWidth, lineStyle);
    this.context.restore();
    return path;
  }

  getRect(x: number, y: number, width: number, height: number, radius?: number) {
    const path = new Path2D();
    if (radius) {
      path.roundRect(x, y, width, height, radius);
    } else {
      path.rect(x, y, width, height);
    }
    return path;
  }

  drawLine(startX: number, startY: number, endX: number, endY: number, color?: ColorLib.ColorInput, lineWidth?: number, lineStyle?: "solid" | number[]) {
    let strokeColor: ColorLib.ColorInput;
    if (color != null) {
      strokeColor = color;
    } else {
      strokeColor = "#000";
    }
    this.context.save();
    const path = new Path2D();
    path.moveTo(startX, startY);
    path.lineTo(endX, endY);
    this.drawPath(path, null, strokeColor, lineWidth, lineStyle);
    this.context.restore();
    return path;
  }

  drawPolygon(points: Vector2Like[], fill?: ColorLib.ColorInput, stroke?: ColorLib.ColorInput, lineWidth?: number, lineStyle?: "solid" | number[]) {
    this.context.save();
    const path = new Path2D();
    points?.forEach((point, index) => {
      if (index === 0) {
        path.moveTo(point.x, point.y);
      }
      if (index < points.length - 1) {
        const nextPoint = points[index + 1];
        path.lineTo(nextPoint.x, nextPoint.y);
      } else {
        const startPoint = points[0];
        path.lineTo(startPoint.x, startPoint.y);
      }
    });
    this.drawPath(path, fill, stroke, lineWidth, lineStyle);
    this.context.restore();
    return path;
  }

  drawPath(path: Path2D, fill?: ColorLib.ColorInput, stroke?: ColorLib.ColorInput, lineWidth?: number, lineStyle?: "solid" | number[]) {
    if (fill != null) {
      this.context.fillStyle = new ColorLib(fill).toRgbString();
      this.context.fill(path);
    }
    if (stroke != null) {
      if (lineWidth != null) {
        this.context.lineWidth = lineWidth;
      } else {
        this.context.lineWidth = 1;
      }
      if (Array.isArray(lineStyle)) {
        this.context.setLineDash(lineStyle);
      } else {
        this.context.setLineDash([]);
      }
      this.context.strokeStyle = new ColorLib(stroke).toRgbString();

      this.context.stroke(path);
    }
  }

  /** Multiplies the internal transform by the provided transform. */
  applyTransform(matrix: Matrix3) {
    this.transform.multiply(matrix);

    this.updateContextTransform();
  }

  /**
   * Sets the internal transform of the canvas.
   * This transform is applied to anything drawn in the canvas space before it gets rendered in the screen space.
   */
  setTransform(matrix?: Matrix3) {
    // Make sure that the scale of the canvas matches its pixel ratio. Resets this.transform
    this.getActualCanvasSize();
    if (matrix) {
      this.transform.multiply(matrix);
    }

    this.updateContextTransform();
  }

  getScale() {
    const [m11, m12, , m21, m22] = this.homogeneousTransform.elements;
    return new Vector2(Math.sqrt(m11 ** 2 + m12 ** 2), Math.sqrt(m21 ** 2 + m22 ** 2));
  }

  getIsPointInShape(point: Vector2Like, path: Path2D) {
    return this.context.isPointInPath(path, point.x * this.scale, point.y * this.scale);
  }

  getIsPointInStroke(point: Vector2Like, path: Path2D, lineWidth: number) {
    this.context.save();
    this.context.lineWidth = lineWidth;
    const isInStroke = this.context.isPointInStroke(path, point.x * this.scale, point.y * this.scale);
    this.context.restore();
    return isInStroke;
  }

  getCanvasCoordinatesFromMouseEvent(event: MouseEvent) {
    if (event.offsetX && event.currentTarget === this.canvas) {
      return new Vector2(event.offsetX, event.offsetY);
    } else {
      const rect = this.canvas.getBoundingClientRect();
      const elementRelativeX = event.clientX - rect.left;
      const elementRelativeY = event.clientY - rect.top;
      return new Vector2(elementRelativeX, elementRelativeY);
    }
  }

  private updateContextTransform() {
    // NOTE(dlb): You can thank Canvas2D's ridiculous transform API for this nonsense
    // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/transform
    let [m11, m12, , m21, m22, , dx, dy, w] = this.transform.elements;

    m11 = m11 / w;
    m12 = m12 / w;
    m21 = m21 / w;
    m22 = m22 / w;
    dx = dx / w;
    dy = dy / w;
    this.homogeneousTransform.set(m11, m12, 0, m21, m22, 0, dx, dy, 1);
    this.context.setTransform(m11, m12, m21, m22, dx, dy);
  }

  async toBlob(type: string = "image/png"): Promise<Blob> {
    return new Promise((resolve) => {
      this.canvas.toBlob(resolve, type);
    });
  }

  readonly canvas: HTMLCanvasElement;
  readonly context: CanvasRenderingContext2D;
  /** the width of the canvas in css pixels */
  readonly width: number;
  /** the height of the canvas in css pixels */
  readonly height: number;
  private textColor: ColorLib.Instance;
  private textFont: string;
  private textSize: number;
  private textWeight: number | "bold" | "normal";
  /** The global transform state. Matches what's in the canvas' context */
  private readonly transform: Matrix3;
  /** The global transform state guaranteed to be an homogenized 3x3 matrix */
  private readonly homogeneousTransform: Matrix3;
  private scale: number;
}
